import { useCallback, useEffect, useState } from "react";
import { Form, Formik } from "formik";
import { toast } from "react-toastify";
import { useParams } from "react-router-dom";
import { PropagateLoader } from "react-spinners";
import Button from "../../../atoms/Button";
import CardTables from "../../../atoms/CardTables";
import InputField from "../../../atoms/InputField";
import DFTableDocs from "../../../organisms/DF/DFTableDocs";
import DFEtapaHeader from "../../../organisms/clientes/empresas/headers/df/DFEtapaHeader";
import { GET_CASOS_BY_ID } from "../../../../api/apiCasos";
import { GET_RECURSO_BY_CASO } from "../../../../api/apiRecurso";
import { GET_ENTERPRISE_BY_ID } from "../../../../api/apiEmpresas";
import { GET_FISCALIZACION_BY_CASO } from "../../../../api/apiFiscalizacion";
import { GET_JUICIO_BY_CASO, UPDATE_JUICIO } from "../../../../api/apiJuicio";
import { GET_CONTRACTUAL_DOCS_BY_CASO } from "../../../../api/apiContractualDocs";
import { useSession } from "../../../../context/SessionContext";

const CONTRACTUAL_FIELDS = {
  minutas: "minutes",
  cotizacion: "quotation",
  contract: "service_contracts",
};

const DFEtapaTemplate = ({ id }) => {
  const { caso_id, etapa } = useParams();
  const { user } = useSession();
  const [caso, setCaso] = useState(null);
  const [empresa, setEmpresa] = useState(null);

  const [data, setData] = useState([]);
  const [etapaId, setEtapaId] = useState(null);

  const [juicio, setJuicio] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingEtapa, setLoadingEtapa] = useState(false);

  const getCasos = useCallback(async () => {
    try {
      setLoading(true);
      const casoData = await GET_CASOS_BY_ID(caso_id);
      setCaso(casoData ?? null);
    } catch (error) {
      console.error(error);
      toast.error("No se pudo obtener el caso.");
    } finally {
      setLoading(false);
    }
  }, [caso_id]);

  const fetchEtapaData = useCallback(async () => {
    if (!caso_id) return;

    const fetchers = {
      juicio: GET_JUICIO_BY_CASO,
      fiscalizacion: GET_FISCALIZACION_BY_CASO,
      recurso: GET_RECURSO_BY_CASO,
      minutas: GET_CONTRACTUAL_DOCS_BY_CASO,
      cotizacion: GET_CONTRACTUAL_DOCS_BY_CASO,
      contract: GET_CONTRACTUAL_DOCS_BY_CASO,
    };

    const fetcher = fetchers[etapa];

    if (!fetcher) {
      setData([]);
      toast.error("Etapa inválida. Usa juicio, fiscalizacion, recurso, minutas, cotizacion o contract.");
      return;
    }

    try {
      setLoadingEtapa(true);

      const etapaRespRaw = await fetcher(caso_id);

      const etapaResp = Array.isArray(etapaRespRaw)
        ? (etapaRespRaw[0] ?? null)
        : (etapaRespRaw ?? null);

      const idRes = etapaResp?.id ?? "";

      if (etapa === "juicio" || etapa === "fiscalizacion" || etapa === "recurso") {
        if (etapa === "juicio") setJuicio(etapaResp ?? null);

        const files = Array.isArray(etapaResp?.files)
          ? etapaResp.files
          : etapaResp?.files
          ? [etapaResp.files]
          : [];

        setData(files);
        setEtapaId(idRes);
        return;
      }

      const field = CONTRACTUAL_FIELDS[etapa];
      if (!field) {
        setData([]);
        toast.error("Etapa inválida. Usa juicio, fiscalizacion, recurso, minutas, cotizacion o contract.");
        return;
      }

      const payload = etapaResp?.[field];
      const normalized = Array.isArray(payload) ? payload : payload ? [payload] : [];

      setData(normalized);
      setEtapaId(idRes);
    } catch (error) {
      console.error(error);
      setData([]);
      toast.error("No se pudo obtener la información de la etapa.");
    } finally {
      setLoadingEtapa(false);
    }
  }, [caso_id, etapa]);

  useEffect(() => {
    const getData = async () => {
      try {
        setLoading(true);
        const empresaData = await GET_ENTERPRISE_BY_ID(id);
        setEmpresa(empresaData ?? null);
      } catch (error) {
        console.error(error);
        toast.error("No se pudo obtener la empresa.");
      } finally {
        setLoading(false);
      }
    };
    getData();
  }, [id]);

  useEffect(() => {
    getCasos();
  }, [getCasos]);

  useEffect(() => {
    fetchEtapaData();
  }, [fetchEtapaData]);

  if (loading || loadingEtapa) {
    return (
      <div className="h-screen w-full flex justify-center items-center">
        <PropagateLoader color="red" />
      </div>
    );
  }

  const initialValues = {
    file_number: juicio?.file_number || "",
    speaker: juicio?.speaker || "",
    secretary: juicio?.secretary || "",
    final_judgment: juicio?.final_judgment || "",
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      await UPDATE_JUICIO(juicio.id, values);
      toast.success("Registro exitoso");
      fetchEtapaData();
    } catch (error) {
      console.error(error);
      toast.error("Error al registrar");
    } finally {
      setSubmitting(false);
    }
  };

  const disabledUser = user && user?.role !== "Client" ? false : true;

  return (
    <div className="flex flex-col gap-4">
      <DFEtapaHeader
        empresa={empresa}
        caso={caso}
        etapa={etapa}
        etapaId={etapaId}
        refetch={fetchEtapaData}
        lastData={data}
        user={user}
      />

      {etapa === "juicio" && (
        <Formik initialValues={initialValues} onSubmit={handleSubmit}>
          {({ isSubmitting }) => (
            <Form className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <InputField name="file_number" label="Número de expediente" type="number" disabled={disabledUser} />
              <InputField name="speaker" label="Ponente" disabled={disabledUser} />
              <InputField name="secretary" label="Secretario" disabled={disabledUser} />
              <InputField name="final_judgment" label="Sentencia definitiva" disabled={disabledUser} />
              <div className="col-span-2 flex justify-center items-center">
                <Button
                  text={isSubmitting ? <PropagateLoader color="red" /> : "Actualizar" }
                  type="submit"
                  bgColor="blue"
                  disabled={isSubmitting || disabledUser}
                />
              </div>
            </Form>
          )}
        </Formik>
      )}

      <CardTables>
        <DFTableDocs
          data={data ?? []}
          loading={loadingEtapa || loading}
          refetch={fetchEtapaData}
          etapaId={etapaId}
          user={user}
        />
      </CardTables>
    </div>
  );
};

export default DFEtapaTemplate;
